import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Bell, LogOut, Sparkles, Target, Gift, X, Plus, ClipboardList, Landmark, ShieldCheck, User as UserIcon, FolderSync } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import NotificationsBell from "@/components/app/NotificationsBell";
import api from "@/lib/api";
import { useCallTracker } from "@/components/app/CallTracker";

/**
 * Mobile-first shell for Office Staff dashboards. Reuses NotificationsBell +
 * AuthContext, but replaces DashLayout with a compact header + drawer + bottom
 * navigation optimised for phones. Desktop keeps the same 3-tab experience
 * but rendered inside this new frame so both stay in sync.
 */
export default function OfficeStaffShell({
  nav,
  activeKey,
  onNav,
  onAddCustomer,
  onReferral,
  stats,
  children,
}) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [dismissTarget, setDismissTarget] = useState(false);
  const [referralBonus, setReferralBonus] = useState(500);
  const [pendingRequests, setPendingRequests] = useState(0);
  const [movingCount, setMovingCount] = useState(0);
  const { overdueCount } = useCallTracker();

  useEffect(() => {
    api.get("/staff/my-target").then(r => setTarget(r.data)).catch(() => {});
    api.get("/settings/referral").then(r => setReferralBonus(r.data.referral_bonus_amount || 500)).catch(() => {});
  }, []);

  // Live-poll pending bank-assignment requests + moving-files count →
  // drives the red dot badges on the bottom-nav "Bank" / "Moving" tabs.
  useEffect(() => {
    let mounted = true;
    const fetchCounts = () => {
      api.get("/staff/bank-requests").then(r => {
        if (mounted) setPendingRequests((r.data.requests || []).length);
      }).catch(() => {});
      api.get("/staff/moving-files").then(r => {
        if (mounted) setMovingCount(r.data.count || 0);
      }).catch(() => {});
    };
    fetchCounts();
    const t = setInterval(fetchCounts, 30000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const doLogout = () => { logout(); navigate("/login"); };

  const bottomNav = useMemo(() => {
    // Show up to 4 primary tabs from parent nav + Profile shortcut (5 total).
    return [...nav.slice(0, 4), { key: "__profile", label: "Profile", icon: UserIcon }];
  }, [nav]);

  return (
    <div className="min-h-screen bg-slate-950 grain flex flex-col">
      {/* Sticky top header */}
      <header className="sticky top-0 z-40 bg-slate-950/95 backdrop-blur-xl border-b border-white/5" data-testid="office-shell-header">
        <div className="flex items-center gap-3 px-4 h-14">
          {/* Hamburger — mobile & desktop */}
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="hamburger-btn"
                className="inline-flex items-center justify-center w-10 h-10 rounded-lg text-amber-400 hover:bg-amber-500/10 transition-colors">
                <Menu className="w-5 h-5"/>
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-slate-950 border-r border-white/10 text-white p-0" data-testid="office-drawer">
              <div className="p-6 border-b border-white/5 flex items-center gap-3">
                <img
                  src="/logo-mark-round.png"
                  alt="Sakthi"
                  className="w-10 h-10 rounded-lg bg-white p-0.5 border border-amber-500/40 object-contain shadow-lg shadow-amber-500/10"
                />
                <div>
                  <div className="font-display text-lg text-white leading-none">Sakthi</div>
                  <div className="text-[10px] tracking-[0.25em] text-amber-500 uppercase mt-1">OFFICE STAFF</div>
                </div>
              </div>
              <nav className="px-3 py-4 space-y-1">
                {nav.map(n => {
                  const showDot = (n.key === "bank_requests" && pendingRequests > 0) ||
                                  (n.key === "leads" && overdueCount > 0);
                  const dotCount = n.key === "bank_requests" ? pendingRequests : overdueCount;
                  const dotTestId = n.key === "bank_requests" ? "drawer-bank-requests-dot" : "drawer-overdue-dot";
                  return (
                    <button
                      key={n.key}
                      data-testid={`drawer-nav-${n.key}`}
                      onClick={() => { onNav(n.key); setDrawerOpen(false); }}
                      className={`relative w-full text-left px-4 py-3 rounded-lg text-sm flex items-center gap-3 transition-colors ${
                        activeKey === n.key ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                      }`}>
                      {n.icon && <n.icon className="w-4 h-4"/>}
                      <span className="flex-1">{n.label}</span>
                      {showDot && (
                        <span data-testid={dotTestId}
                          className="min-w-[20px] h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center px-1.5 leading-none">
                          {dotCount > 9 ? "9+" : dotCount}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
              <div className="p-4 border-t border-white/5 mt-auto absolute bottom-0 left-0 right-0">
                <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Signed in</div>
                <div className="text-white text-sm font-medium">{user?.name || "—"}</div>
                <div className="text-xs text-slate-500 font-mono-alt">{user?.phone}</div>
                <Button onClick={doLogout} data-testid="drawer-logout" size="sm" variant="ghost"
                  className="mt-3 w-full text-slate-400 hover:text-red-400 hover:bg-red-500/10 justify-start">
                  <LogOut className="w-4 h-4 mr-2"/> Logout
                </Button>
              </div>
            </SheetContent>
          </Sheet>

          {/* Center: role + name */}
          <Link to="/dashboard" className="flex-1 min-w-0 text-center">
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-500 font-bold leading-none">Sakthi · Office Staff</div>
            <div className="font-heading text-sm text-white mt-1 truncate">
              {user?.name || "Staff"}
              <span className="text-slate-500 font-mono-alt ml-2 text-xs">{user?.phone}</span>
            </div>
          </Link>

          {/* Right: notification bell */}
          <div className="w-10 h-10 flex items-center justify-center">
            <NotificationsBell/>
          </div>
        </div>

        {/* Small target banner — appears only when no target and not dismissed */}
        {target && !target.target && !dismissTarget && (
          <div className="px-4 pb-3 -mt-1" data-testid="target-banner">
            <div className="rounded-lg bg-amber-500/10 border border-amber-500/25 px-3 py-2 text-xs flex items-center gap-2">
              <Target className="w-3.5 h-3.5 text-amber-400 shrink-0"/>
              <span className="text-amber-100 flex-1 min-w-0 truncate">No target set for {target.month} — ask admin to set one</span>
              <button onClick={() => setDismissTarget(true)} className="text-amber-300/70 hover:text-amber-200 shrink-0" data-testid="dismiss-target">
                <X className="w-3.5 h-3.5"/>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="flex-1 pb-24 md:pb-8">
        {/* Stats strip — horizontally scrollable on mobile */}
        {stats && (
          <div className="px-4 pt-4">
            <div className="flex gap-3 overflow-x-auto no-scrollbar snap-x snap-mandatory md:grid md:grid-cols-4 md:overflow-visible" data-testid="stats-strip">
              {stats.map((s, i) => (
                <div
                  key={s.key}
                  data-testid={`stat-${s.key}`}
                  className={`snap-start shrink-0 min-w-[9rem] md:min-w-0 rounded-xl border p-3 ${s.tone}`}>
                  <div className="text-[10px] uppercase tracking-widest opacity-75">{s.label}</div>
                  <div className="font-display text-3xl mt-0.5 leading-none">{s.value}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compact referral banner */}
        <div className="px-4 mt-3" data-testid="referral-banner">
          <div className="rounded-xl bg-gradient-to-r from-amber-500/15 to-amber-500/5 border border-amber-500/30 px-4 py-3 flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-amber-500/20 flex items-center justify-center shrink-0">
              <Gift className="w-4 h-4 text-amber-400"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-white leading-tight">Refer & Earn ₹{Number(referralBonus).toLocaleString("en-IN")}</div>
              <div className="text-[11px] text-amber-100/70 leading-tight mt-0.5">Add a customer reference and earn on disbursal.</div>
            </div>
            <button
              type="button"
              data-testid="referral-add-btn"
              onClick={onReferral}
              className="shrink-0 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 text-xs font-bold px-3 py-1.5 whitespace-nowrap">
              Add Reference
            </button>
          </div>
        </div>

        {/* Page body */}
        <div className="px-4 pt-4 md:px-6">
          {children}
        </div>
      </main>

      {/* Floating Add Customer button */}
      {onAddCustomer && (
        <button
          type="button"
          data-testid="fab-add-customer"
          onClick={onAddCustomer}
          className="fixed bottom-40 md:bottom-6 right-4 md:right-6 z-40 h-14 w-14 rounded-full bg-amber-500 hover:bg-amber-400 text-slate-900 shadow-xl shadow-amber-500/30 flex items-center justify-center transition-transform active:scale-95 gold-glow">
          <Plus className="w-6 h-6"/>
        </button>
      )}

      {/* Bottom nav — mobile only. 5-tab layout: My · In Pool · Moving · Bank · Profile */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-slate-950/95 backdrop-blur-xl border-t border-white/10" data-testid="bottom-nav">
        <div className={`grid ${bottomNav.length === 5 ? "grid-cols-5" : "grid-cols-4"}`}>
          {bottomNav.map(n => {
            const isActive = activeKey === n.key;
            const onClick = n.key === "__profile" ? () => setDrawerOpen(true) : () => onNav(n.key);
            const showDot = (n.key === "bank_requests" && pendingRequests > 0) ||
                            (n.key === "leads" && overdueCount > 0) ||
                            (n.key === "moving" && movingCount > 0);
            const dotCount = n.key === "bank_requests" ? pendingRequests
                            : n.key === "moving" ? movingCount
                            : overdueCount;
            const dotTestId = n.key === "bank_requests" ? "bank-requests-dot"
                             : n.key === "moving" ? "moving-dot"
                             : "overdue-dot";
            const dotCls = n.key === "moving"
              ? "bg-orange-500 text-slate-900"
              : "bg-red-500 text-white";
            const shortLabel = n.key === "home" ? "Home"
                             : n.key === "leads" ? "My"
                             : n.key === "in_bank_pool" ? "In Pool"
                             : n.key === "moving" ? "Moving"
                             : n.key === "bank_requests" ? "Bank"
                             : n.label.split(" ")[0];
            return (
              <button
                key={n.key}
                type="button"
                onClick={onClick}
                data-testid={`bottom-nav-${n.key}`}
                className={`relative flex flex-col items-center gap-0.5 py-2.5 transition-colors ${isActive ? "text-amber-400" : "text-slate-400"}`}>
                <div className="relative">
                  {n.icon && <n.icon className="w-4 h-4"/>}
                  {showDot && (
                    <span
                      data-testid={dotTestId}
                      className={`absolute -top-1.5 -right-2 min-w-[16px] h-4 rounded-full text-[9px] font-bold flex items-center justify-center px-1 leading-none animate-pulse ${dotCls}`}>
                      {dotCount > 9 ? "9+" : dotCount}
                    </span>
                  )}
                </div>
                <span className={`text-[10px] font-semibold ${isActive ? "" : "opacity-70"}`}>{shortLabel}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
